import { Throwable } from "@wss/error-tracking/throwable";
import { GroupMember, Product } from "../../common/Store/models/ProductListing";
import { doesAnyGroupMembersHasMustPurchaseMoreThanOne } from "./AddToCart/logic";
import ValidationError from "./ValidationError";

export const minimumQuantity = ({
  product,
  quantity,
  groupingValue,
}: {
  product: Product;
  quantity: number;
  groupingValue?: string;
}) => {
  let { startBuy } = product;

  const hasVGItems =
    groupingValue === "" && product.groupingAttributes.groupMembers.length > 0;

  if (groupingValue) {
    startBuy =
      product.groupingAttributes.groupMembers.find(
        (x) => x.itemNumber === groupingValue
      )?.startBuy || 1;
  }

  const quantityDisplayVisible =
    product.mustPurchaseMoreThanOne ||
    doesAnyGroupMembersHasMustPurchaseMoreThanOne(product.groupingAttributes);

  if (quantity < startBuy && (!quantityDisplayVisible || !hasVGItems)) {
    throw new ValidationError(
      `Please enter a quantity of ${startBuy} or more.`,
      minimumQuantity
    );
  }
};

export const mustBuy = ({
  product,
  quantity,
  groupingValue,
}: {
  product: Product;
  quantity: number;
  groupingValue?: string;
}) => {
  let mustBuyQuantity = product.mustBuy;

  if (groupingValue) {
    mustBuyQuantity =
      product.groupingAttributes.groupMembers.find(
        (x) => x.itemNumber === groupingValue
      )?.mustBuy || 1;
  }

  if (quantity % mustBuyQuantity) {
    throw new ValidationError(
      `Please enter a quantity as a multiple of ${mustBuyQuantity}`,
      mustBuy
    );
  }
};

export const groupingSelected = ({
  product,
  groupingValue,
}: {
  product: Product;
  groupingValue?: string;
}) => {
  if (!product.grouping) return;

  const validationMessage = "Please select required product options.";

  if (
    product.groupingAttributes.isVirtual &&
    product.groupingAttributes.groupMembers.length > 0 &&
    product.groupingAttributes.groupMembers.some(
      (member: GroupMember) =>
        member.configurationOptions
          .map((option) => option.name)
          .filter((value, index, self) => self.indexOf(value) === index)
          .length > 1
    )
  ) {
    return;
  }

  if (!groupingValue) {
    throw new ValidationError(validationMessage, groupingSelected);
  }
};

export type Validations =
  | typeof groupingSelected
  | typeof mustBuy
  | typeof minimumQuantity
  | undefined;

interface Params {
  product: Product;
  quantity: number;
  groupingValue?: string;
  validationsToRun?: (({
    product,
    groupingValue,
    quantity,
  }: {
    product: Product;
    groupingValue?: string;
    quantity: number;
  }) => void)[];
}
/**
 * Runs validations, if any fail the setError function that's passed in will be called
 * with the failing validation error as the argument.
 */
export const valid = ({
  product,
  quantity,
  groupingValue,
  validationsToRun = [groupingSelected, minimumQuantity, mustBuy],
}: Params): Throwable => {
  try {
    for (const validation of validationsToRun) {
      validation({ product, quantity, groupingValue });
    }

    return { isError: false };
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return { isError: true, error: error as ValidationError };
  }
};
